import {
  DEFAULT_PIPELINE_DEFINITION,
  getApprovablePhases,
  getPhaseLabels,
  getPhaseOrder,
  getStartablePhases,
} from '@internal/spassu-code-contract';
import type {
  Role,
  RbacAction,
  ActivityStatus,
  ActivityExecutionMode,
  ActivityHandoffProjection,
  ActivityHandoffState,
  ActivitySource,
  ActivityWorkPath,
  CanonicalTimelineEvent,
  IssueStateCategory,
  ManagementTimelineQuery,
  ObservabilityFidelity,
  ObservabilityProvenance,
  ObservabilitySource,
  Phase,
  PhaseConformitySummary,
  ProviderId,
  SkillInvocationProjection,
  SkillOrigin,
  SkillRegistrationType,
  ControlPlaneOperationalProjection,
} from '@internal/spassu-code-contract';

export type {
  ActivityStatus,
  ActivityExecutionMode,
  ActivityHandoffProjection,
  ActivityHandoffState,
  ActivityJourneyStep,
  ActivityJourneyStepKind,
  ActivityJourneyStepState,
  ActivityManagementJourney,
  ActivityManagementNextAction,
  ActivityManagementNextActionKind,
  ActivityManagementSessionCoverage,
  ActivityManagementSessionCoverageGap,
  ActivityManagementSessionCoverageState,
  ActivityManagementSessionEmitterHealth,
  ActivityManagementSessionPairingHealth,
  ActivityManagementSituation,
  ActivityManagementSkillRow,
  ActivityManagementSummaryPage,
  ActivityManagementTelemetryWarning,
  ActivityManagementStepDetail,
  ActivityManagementTimelineGroup,
  ActivitySource,
  ActivityWorkPath,
  AutoStartPolicy,
  ApprovalPolicy,
  CanonicalTimelineEvent,
  ManagementTimelineFilters,
  ManagementTimelineMarker,
  ManagementTimelineMarkerType,
  ManagementTimelineOutcome,
  ManagementTimelinePage,
  ObservabilityBoardProjection,
  ObservabilityFidelity,
  ObservabilityProvenance,
  ObservabilitySource,
  Phase,
  PhaseTransitionRule,
  PipelineDefinition,
  PipelinePhaseDefinition,
  SkillInvocationProjection,
  SkillOrigin,
  SkillRegistrationType,
  TraceArtifactCategory,
  TraceArtifactCategorySummary,
  TraceArtifactContentPage,
  TraceArtifactCollection,
  TraceArtifactConfidence,
  TraceArtifactEmptyState,
  TraceArtifactItem,
  TraceArtifactParticipantRef,
  TraceArtifactStatus,
  TraceAuditItem,
  TraceAuditItemCategory,
  TraceAuditTrailPage,
  TraceDoraActivityPage,
  TraceDoraEvidence,
  TraceDoraIndicator,
  TraceDoraIndicatorKey,
  TraceDoraIndicatorState,
  TraceDeliveryCard,
  TraceDeliveryEntry,
  TraceOtherEvidenceRow,
  TraceDeliveryQuality,
  TraceDeliveryKind,
  TraceParticipant,
  TraceParticipantContributionKind,
  TraceParticipantKind,
  TraceParticipantPeriod,
  TraceParticipantRole,
  TraceParticipantsAbsenceState,
  TraceParticipantsPage,
  TraceQualityEvidence,
  TraceQualityItem,
  TraceQualityProvenance,
  TraceQualityState,
  // Multi-provider SCM/Boards types (Tarefa 5+)
  Issue,
  IssueQuery,
  IssueScope,
  IssueStateCategory,
  IssueSearchResult,
  ProviderId,
  Repository,
  RepositoryBranch,
  RepositoryHierarchy,
  RepositoryOwner,
  RepositoryProject,
} from '@internal/spassu-code-contract';

/**
 * Provider descriptor surfaced by `GET /scm/providers`. Drives the frontend's
 * adaptive picker — when only one is enabled we render it silently.
 */
export interface ScmProviderDescriptor {
  id: import('@internal/spassu-code-contract').ProviderId;
  displayName: string;
  hierarchy: import('@internal/spassu-code-contract').RepositoryHierarchy;
}

/**
 * Projeto gerenciável na tela "Gestão de Projetos". Cada item equivale a um
 * `project_key` no esquema RBAC agnóstico (= owner do provider). Para Azure
 * DevOps, esse owner é a organização configurada; para GitHub, a org/usuário.
 */
export interface ManagedProject {
  /** project_key usado em todas as APIs RBAC. */
  name: string;
  /** Provider de origem — usado para agrupar/filtrar a UI. */
  providerId: import('@internal/spassu-code-contract').ProviderId;
  providerLabel: string;
}

export type ActivityDeliveryOutcome = 'delivered' | 'closed_without_delivery';

export type CloneStatus = 'pending' | 'cloning' | 'success' | 'failed';

export type ActivityHealthState =
  | 'healthy'
  | 'projecting'
  | 'degraded'
  | 'blocked'
  | 'recovery-needed';

export type ActivityHealthActionCode =
  | 'none'
  | 'run_doctor'
  | 'reconnect_backend'
  | 'retry_sync'
  | 'inspect_projection';

export interface ActivityHealthSignal {
  state: ActivityHealthState;
  reasonCode: string;
  summary: string;
  details?: Record<string, unknown>;
}

export interface ActivityCurrentHealth {
  checkpoint: string;
  localHealth: ActivityHealthSignal;
  remoteHealth: ActivityHealthSignal;
  overallHealth: ActivityHealthSignal;
  actionCode: ActivityHealthActionCode;
  actionText: string | null;
  emittedAt: string;
}

export interface ActivityWorkItem {
  id: number;
  type: string;
  url: string;
  title: string;
  state: string;
}

export interface ActivityIssue {
  externalId: string;
  type: string;
  title: string;
  state: string;
  stateCategory: IssueStateCategory | null;
  url: string;
}

export interface Activity {
  id: string;
  userId: string;
  title: string;
  description: string;
  /** @deprecated Use `repoProject` (will be null for providers without project layer). */
  azureProject: string;
  /** @deprecated Use `repoName`. */
  azureRepository: string;
  branch: string | null;
  clonePath: string | null;
  cloneStatus?: CloneStatus | null;
  cloneError?: string | null;
  clonedAt?: string | null;
  currentPhase: Phase;
  status: ActivityStatus;
  errorMessage?: string | null;
  executionMode: ActivityExecutionMode;
  source?: ActivitySource;
  workPath?: ActivityWorkPath;
  handoffState?: ActivityHandoffState;
  handoff?: ActivityHandoffProjection;
  commitsJson?: string | null;
  prUrl?: string | null;
  deliveryOutcome?: ActivityDeliveryOutcome | null;
  /** @deprecated Use `issue`. */
  azureWorkItem?: ActivityWorkItem | null;
  /** Provider-agnostic identification (multi-provider). */
  provider?: string;
  providerExternalId?: string | null;
  repoOwner?: string | null;
  repoProject?: string | null;
  repoName?: string | null;
  issue?: ActivityIssue | null;
  currentHealth?: ActivityCurrentHealth | null;
  /** Evidence summary consumed by the existing CTA in a later rollout. */
  phaseConformity?: PhaseConformitySummary | null;
  /**
   * AI engine usado para executar fases (C5). Default 'claude-code'.
   * Codex tem suporte limitado — fases skill-dependentes (dividindo-tasks,
   * executando, pronto) ficam bloqueadas pela UI quando engine='codex' (D2).
   */
  aiEngine?: 'claude-code' | 'codex';
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string | null;
  /**
   * Canonical Control Plane projection, attached by the portfolio list in one
   * batched read. Absent for non-Control-Plane activities.
   */
  controlPlane?: ControlPlaneOperationalProjection;
}

export interface ChatMessage {
  id: string;
  activityId: string;
  phase: Phase;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface PhaseMetrics {
  id: string;
  activityId: string;
  phase: Phase;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCost: number;
  latencyMs: number;
  agentRole: string;
  startedAt: string;
  completedAt: string | null;
}

export interface Artifact {
  id: string;
  activityId: string;
  phase: Phase;
  artifactType: string;
  content: string;
  filePath?: string;
  approved: boolean;
  approvedBy?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;
  createdAt: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
}

export interface Approach {
  title: string;
  description: string;
  pros: string[];
  cons: string[];
  tradeoffs: string[];
  recommended: boolean;
}

export interface ReviewIssue {
  severity: 'error' | 'warning' | 'info';
  file: string;
  line?: number;
  message: string;
  rule: string;
}

export interface ReviewResult {
  passed: boolean;
  issues: ReviewIssue[];
  summary: string;
}

export interface AzureProject {
  id: string;
  name: string;
  description?: string;
}

export interface AzureRepository {
  id: string;
  name: string;
  defaultBranch?: string;
  remoteUrl?: string;
  webUrl?: string;
}

export interface CreateActivityInput {
  title: string;
  description?: string;
  /** @deprecated Use `repoProject` (set explicitly to `null` for providers without project layer). */
  azureProject?: string;
  /** @deprecated Use `repoName`. */
  azureRepository?: string;
  branch?: string;
  workPath: ActivityWorkPath;
  // Azure Boards Work Item (optional — absent means "created from scratch")
  /** @deprecated Use `issueExternalId`. */
  azureWorkItemId?: number;
  /** @deprecated Use `issueType`. */
  azureWorkItemType?: string;
  /** @deprecated Use `issueUrl`. */
  azureWorkItemUrl?: string;
  /** @deprecated Use `issueTitle`. */
  azureWorkItemTitle?: string;
  /** @deprecated Use `issueState`. */
  azureWorkItemState?: string;
  // Provider-agnostic shape (multi-provider). When `provider` is anything
  // other than 'azure-devops' the agnostic fields are required.
  provider?: ProviderId;
  repoOwner?: string;
  repoProject?: string | null;
  repoName?: string;
  issueExternalId?: string;
  issueType?: string;
  issueTitle?: string;
  issueState?: string;
  issueUrl?: string;
  /** AI engine selecionada (D2 — Workstream C/D). Default 'claude-code' aplicado pelo backend. */
  aiEngine?: 'claude-code' | 'codex';
}

export interface AzureBranch {
  name: string;
  isDefault: boolean;
}

export interface ActivityFilters {
  status?: ActivityStatus;
  workPath?: ActivityWorkPath;
  azureProject?: string;
  azureRepository?: string;
  source?: string;
  issueExternalId?: string;
  createdBy?: string;
  createdFrom?: string;
  createdToExclusive?: string;
  page?: number;
  limit?: number;
  mineOnly?: boolean;
}

export interface ActivityFilterOptions {
  projects: string[];
  repositories: string[];
  creators: string[];
}

export interface DeleteIncompleteActivitiesResult {
  deletedCount: number;
  cloneCleanupErrors: number;
  releasedExecutionModeCount?: number;
}

export interface ReleaseCommandResult {
  success: true;
  previousExecutionMode: ActivityExecutionMode;
  releasedAt: string;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const PHASES_ORDER: Phase[] = getPhaseOrder(DEFAULT_PIPELINE_DEFINITION);
export const PHASE_LABELS: Record<Phase, string> = getPhaseLabels(
  DEFAULT_PIPELINE_DEFINITION,
);

export const STATUS_LABELS: Record<ActivityStatus, string> = {
  preparing: 'Preparando',
  active: 'Ativa',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  error: 'Erro',
};

export const APPROVABLE_PHASES: Phase[] = getApprovablePhases(
  DEFAULT_PIPELINE_DEFINITION,
);
export const ELIGIBLE_PHASES: Phase[] = getStartablePhases(
  DEFAULT_PIPELINE_DEFINITION,
);

export interface QuestionOption {
  label: string;
  description: string;
}

export interface Question {
  question: string;
  header: string;
  options: QuestionOption[];
  multiSelect: boolean;
}

export interface AskUserQuestionInput {
  questions: Question[];
}

export type InteractionType = 'session' | 'question' | 'approach';

export type InteractionStatus =
  | 'streaming'
  | 'awaiting_user'
  | 'expired'
  | 'completed'
  | 'canceled';

export type InteractionResumeMode = 'direct' | 'summary_required';

export type ResumeStreamConflictReason =
  | 'no_inflight_stream'
  | 'interaction_mismatch'
  | 'worker_disconnected';

export interface ResumeStreamConflictInteractionState {
  status: InteractionStatus;
  resumeMode: InteractionResumeMode;
  terminalReason: string | null;
}

export interface ResumeStreamConflict {
  error: string;
  reason?: ResumeStreamConflictReason;
  retryable?: boolean;
  interactionState?: ResumeStreamConflictInteractionState;
}

export interface InteractionState {
  id: string;
  activityId: string;
  phase: Phase;
  type: InteractionType;
  status: InteractionStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  resumeMode: InteractionResumeMode;
  resumeWindowEndsAt: string | null;
  resolvedAt: string | null;
  lastRecoveredAt: string | null;
  terminalReason: string | null;
  canResume: boolean;
  recoverySnapshotContent?: string;
  questions?: Question[];
  approaches?: { question: string; approaches: Approach[] };
}

export interface AnswerInteractionResult {
  success: boolean;
  resumeMode: InteractionResumeMode;
  resumeMessage?: string;
}

export type TaskStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'error'
  | 'paused';

export type ExecutionState = 'idle' | 'running' | 'paused';

export interface TaskItem {
  id: string;
  activityId: string;
  taskNumber: string;
  title: string;
  filePath?: string | null;
  dependsOn?: string[];
  status: TaskStatus;
  executionLog: string | null;
  errorMessage: string | null;
  /** Real worker signal: true only after merge/reconciliation confirms integration. */
  integrated?: boolean | null;
  integratedAt?: string | null;
  parallelSlot?: number | null;
  totalSlots?: number | null;
  worktreePath?: string | null;
  /** UI-only timestamp derived from live task events; not persisted by backend. */
  lastEventAt?: string | null;
  /**
   * RF5: heartbeat durável do backend (último evento recebido do worker). Fonte
   * de verdade do "sem atividade", independente do feed SSE ao vivo.
   */
  lastWorkerEventAt?: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/**
 * Reexportado do contrato compartilhado: a cópia local desta interface omitia
 * `capabilities` e não tinha verificação de equivalência com o servidor.
 */
export type { WorkerStatus } from '@internal/spassu-code-contract';

export interface WorkerToken {
  token: string;
  userId: string;
  ttlSeconds: number;
  expiresAt: string;
}

export interface StreamEvent {
  type:
    | 'text'
    | 'tool_use'
    | 'ask_user_question'
    | 'metrics'
    | 'phase_complete'
    | 'phase_auto_transition'
    | 'activity_complete'
    | 'approaches'
    | 'review_started'
    | 'review_result'
    | 'error'
    | 'rate_limit'
    | 'done'
    | 'task_started'
    | 'task_progress'
    | 'task_completed'
    | 'task_integrated'
    | 'task_error'
    | 'worktree_lifecycle'
    | 'tasks_discovered'
    | 'execution_complete'
    | 'execution_paused'
    | 'phase_conformity_repair_required'
    | 'phase_conformity_repair_unavailable';
  /** Sequential event ID from SSE `id:` field — used for deterministic replay on reconnect. */
  seqId?: number;
  content: string;
  metrics?: Partial<PhaseMetrics>;
  questions?: AskUserQuestionInput;
  approaches?: { question: string; approaches: Approach[] };
  review?: ReviewResult;
  phase?: string;
  nextPhase?: Phase;
  taskNumber?: string;
  taskTitle?: string;
  taskChunkType?: 'text' | 'tool_use';
  errorMessage?: string;
  integrated?: boolean;
  lifecycleAction?: string;
  branch?: string;
  branchAction?: string;
  reason?: string;
  tasks?: Array<{ taskNumber: string; title: string; dependsOn?: string[] }>;
  parallelSlot?: number;
  totalSlots?: number;
  worktreePath?: string;
  phaseAttemptId?: string;
  missingReasonCodes?: string[];
  missingConcepts?: string[];
  code?: string;
  resetTime?: string;
  timezone?: string;
}

// ─── Skill Vault Types ───

export interface SkillSummary {
  id: string;
  name: string;
  version: string | null;
  description: string | null;
  phase: string | null;
  content: string;
  signature: string | null;
  signedBy: string | null;
  signedAt: string | null;
  status: string;
  origin: string;
  reviewerId: string | null;
  approvedAt: string | null;
  usageCount: number;
  avgQualityScore: number | null;
  avgTokensSaved: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SkillEffectivenessEntry {
  id: string;
  name: string;
  phase: string | null;
  usageCount: number;
  avgQualityScore: number | null;
  avgTokensSaved: number | null;
}

export interface DryRunResult {
  systemPrompt: string;
  userMessage: string;
  skillName: string | null;
  tokenEstimate: number;
  costEstimate: number;
  model: string;
  contextSections: string[];
}

// ─── CLI Activity Page Types ───

export interface TimelineEvent extends CanonicalTimelineEvent {}

export interface TimelineQueryOptions {
  source?: ObservabilitySource;
  eventType?: string;
  cursor?: string;
  limit?: number;
}

export interface TimelinePage {
  events: TimelineEvent[];
  nextCursor: string | null;
}

export interface SkillInvocationProjectionPage {
  activityId: string;
  generatedAt: string;
  sourceEventCount: number;
  invocations: SkillInvocationProjection[];
}

export type ManagementTimelineQueryOptions = ManagementTimelineQuery;

export interface SkillUsageSummary {
  skillName: string;
  count: number;
  skillOrigin: SkillOrigin;
  registrationType: SkillRegistrationType;
  source: ObservabilitySource;
  provenance: ObservabilityProvenance;
  fidelity: ObservabilityFidelity;
  provider?: string;
  agentType?: string;
}

export type TraceReliabilityState =
  | 'confirmed'
  | 'calculated'
  | 'estimated'
  | 'partial'
  | 'unavailable'
  | 'not-applicable';

export interface TraceReliabilityIndicator {
  state: TraceReliabilityState;
  label: string;
  source: string;
  period: string;
  coverage: string;
  updatedAt: string | null;
  explanation: string;
  currency?: string;
}

export interface CostSummaryEntry {
  groupKey: string;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalCost: number;
  count: number;
  observability?: {
    fidelities: ObservabilityFidelity[];
    provenances: ObservabilityProvenance[];
    lowFidelityExcluded: boolean;
    excludedLowFidelityCount: number;
    excludedLowFidelityCost: number;
    reliability?: TraceReliabilityIndicator;
  };
}

export interface DORAMetrics {
  cycleTime: { avgDays: number; medianDays: number; count: number };
  deploymentFrequency: { perWeek: number; perMonth: number; total: number };
  changeFailureRate: { rate: number; failed: number; total: number };
  aiCostPerFeature: {
    avgCost: number;
    totalCost: number;
    featureCount: number;
    costedFeatureCount?: number;
  };
  period: { from: string; to: string; label?: string };
  observability?: {
    includedFidelities: ObservabilityFidelity[];
    includedProvenances: ObservabilityProvenance[];
    lowFidelityExcluded: boolean;
    excludedLowFidelityCount: number;
    excludedLowFidelityCost: number;
    completedFeatureCount: number;
    costedFeatureCount: number;
    costTelemetryCoverage: number;
    hasSufficientCostTelemetry: boolean;
    reliability?: Record<string, TraceReliabilityIndicator>;
  };
}

export type ComplianceFramework = 'eu-ai-act' | 'soc2' | 'hipaa' | 'iso-42001';

export interface ComplianceRequirement {
  id: string;
  category: string;
  requirement: string;
  mappedArtifact: string;
  status: 'present' | 'missing' | 'partial';
  evidence: string | null;
}

export interface ComplianceReport {
  framework: ComplianceFramework;
  frameworkName: string;
  activityId: string;
  generatedAt: string;
  overallStatus: 'compliant' | 'partial' | 'non-compliant';
  requirements: ComplianceRequirement[];
  completeness: {
    total: number;
    present: number;
    missing: number;
    partial: number;
    percentage: number;
  };
}

export interface AgentEvent {
  id: string;
  agentId: string;
  activityId: string | null;
  correlationId: string | null;
  eventType: string;
  payload: Record<string, unknown>;
  timestamp: string;
  ingestedAt: string;
}

export interface PaginatedEvents {
  data: AgentEvent[];
  total: number;
  page: number;
  limit: number;
}

// ─── RBAC Types ───

/**
 * Vocabulário RBAC do contrato compartilhado. O import nomeado existe porque os
 * dois tipos continuam em uso neste arquivo, e o reexport preserva quem já
 * importa daqui.
 */
export type { Role, RbacAction };

export interface ProjectMember {
  id: string;
  projectKey: string;
  repositoryKey: string | null;
  entityRef: string;
  entityKind: 'User' | 'Group';
  role: Role;
  assignedBy: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface AccessibleProject {
  projectKey: string;
  roles: Role[];
  repositories: string[];
  allRepositories: boolean;
  canSeeAllActivities: boolean;
}

export interface AuditLogEntry {
  id: string;
  actorRef: string;
  action: string;
  projectKey: string | null;
  resourceType: string | null;
  resourceId: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditLogFilters {
  projectKey?: string;
  action?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  gestor: 'Gestor de Projeto',
  analista: 'Analista de Requisitos',
  desenvolvedor: 'Desenvolvedor',
  revisor: 'Revisor',
  aprovador: 'Aprovador',
  observador: 'Observador',
};

export interface ComplianceMetrics {
  projectKey: string;
  period: { days: number; since: string };
  activities: {
    total: number;
    active: number;
    completed: number;
    errors: number;
  };
  artifacts: { total: number; approved: number; pending: number };
  auditActions: number;
  members: { total: number; temporary: number; expired: number };
  policies: Record<string, unknown>;
}

// ─── Azure Boards — Work Item types ───

export interface AzureWorkItemType {
  name: string;
  referenceName: string;
  description?: string;
  color?: string;
  icon?: string;
  isDisabled?: boolean;
  states?: Array<{ name: string; category: string }>;
}

export interface AzureWorkItemSummary {
  id: number;
  type: string;
  title: string;
  state: string;
  assignedTo?: string;
  areaPath?: string;
  iterationPath?: string;
  tags?: string[];
  url: string;
  parentId?: number;
}

export interface AzureWorkItemDetail extends AzureWorkItemSummary {
  description?: string;
  descriptionText?: string;
  acceptanceCriteria?: string;
  acceptanceCriteriaText?: string;
  reproSteps?: string;
  reproStepsText?: string;
  rev: number;
  parent?: { id: number; type: string; title: string };
}

export interface AzureWorkItemSnapshot {
  description?: string;
  acceptanceCriteria?: string;
  reproSteps?: string;
  tags?: string[];
  areaPath?: string;
  iterationPath?: string;
  assignedTo?: string;
  parent?: { id: number; type: string; title: string };
  capturedAt: string;
  rev: number;
}

export interface SearchWorkItemsFilters {
  projectId: string;
  types?: string[];
  states?: string[];
  assignedToMe?: boolean;
  assignedToRef?: string;
  text?: string;
  workItemId?: number;
  areaPath?: string;
  iterationPath?: string;
  top?: number;
  skip?: number;
}

export interface PaginatedWorkItems {
  data: AzureWorkItemSummary[];
  total: number;
  top: number;
  skip: number;
  hasMore: boolean;
}
